// stores/referenceData.js
import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";

export const useReferenceDataStore = defineStore("referenceData", () => {
  // Reference data containers
  const organizations = ref([]);
  const vehicles = ref([]);
  const drivers = ref([]);
  const travelStatusConfig = ref([]);
  const tripStatusConfig = ref([]);
  const tripBasisTypeConfig = ref([]);
  const clientServiceTypeConfig = ref([]);
  const isLoading = ref(false);
  const isInitialized = ref(false);
  const error = ref(null);

  // Fetch all reference data from a single endpoint
  const fetchReferenceData = async () => {
    if (isLoading.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      const response = await axios.get("/api/configs/reference-data");

      // Assuming the API returns an object with all reference data
      const data = response.data;

      // Set values based on the API response structure
      organizations.value = data.organizations || [];
      vehicles.value = data.vehicles || [];
      drivers.value = data.drivers || [];
      travelStatusConfig.value = data.travelStatusConfig || [];
      tripStatusConfig.value = data.tripStatusConfig || [];
      tripBasisTypeConfig.value = data.tripBasisTypeConfig || [];
      clientServiceTypeConfig.value = data.clientServiceTypeConfig || [];

      // Mark as initialized
      isInitialized.value = true;

      return data;
    } catch (err) {
      console.error("Failed to fetch reference data:", err);
      error.value =
        err.response?.data?.message || "Failed to load reference data";
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Helper method to ensure data is loaded
  const ensureDataLoaded = async () => {
    if (!isInitialized.value && !isLoading.value) {
      return await fetchReferenceData();
    }
    return {
      organizations: organizations.value,
      vehicles: vehicles.value,
      drivers: drivers.value,
      travelStatusConfig: travelStatusConfig.value,
      tripStatusConfig: tripStatusConfig.value,
      tripBasisTypeConfig: tripBasisTypeConfig.value,
      clientServiceTypeConfig: clientServiceTypeConfig.value,
    };
  };

  // Clear all data (e.g., during logout)
  const clearData = () => {
    organizations.value = [];
    vehicles.value = [];
    drivers.value = [];
    travelStatusConfig.value = [];
    tripStatusConfig.value = [];
    tripBasisTypeConfig.value = [];
    clientServiceTypeConfig.value = [];
    isInitialized.value = false;
    error.value = null;
  };

  return {
    // Data
    organizations,
    vehicles,
    drivers,
    travelStatusConfig,
    tripStatusConfig,
    tripBasisTypeConfig,
    clientServiceTypeConfig,
    isLoading,
    isInitialized,
    error,

    // Methods
    fetchReferenceData,
    ensureDataLoaded,
    clearData,
  };
});
